import { createSelector, Selector } from 'reselect'

//import { Column, TextEditor } from 'react-data-grid'
import { IDataGrid2_Column } from '../../components/datagrid2/DataGrid'

import {
  networkGroup,
} from './utils'

import {
  INode_Org,
  INode_User,
  ITree_Role,
} from '@kaidam/fc-shared/src/types/schema/tree'

import {
  IForm_Dynamic,
} from '@kaidam/fc-shared/src/types/schema/form'

import {
  IData_MagicTag_Summary,
} from '@kaidam/fc-shared/src/types/schema/magic_tag'

import {
  IData_Org_With_Profile,
} from '@kaidam/fc-shared/src/types/rbac'

import {
  IData_AppEvent_Type_Summary,
  IRecord_AppEvent_With_UserOrg,
} from '@kaidam/fc-shared/src/types/schema/app_event'

import {
  IMeta_ProjectConfig_App_Config_Rbac,
} from '@kaidam/fc-shared/src/types/schema/project_config'

import {
  IData_Paginated_Results,
} from '@kaidam/fc-shared/src/types/pagination'

import {
  IData_UserSummary_FlatRoleObject,
  IData_RoleSummary_TreeNode,
} from '@kaidam/fc-shared/src/types/schema/user_summary'

import {
  RootState,
} from '../../types/store'

import {
  getRoleSummaryTree,
} from '@kaidam/fc-shared/src/utils/role'

import {
  flattenOrgProfiles,
} from '@kaidam/fc-shared/src/utils/rbac'

import {
  flattenSchema,
} from '@kaidam/fc-shared/src/utils/form'

import {
  getEventTypeTree,
  getEventTypeTreeSelectOptions,
} from '@kaidam/fc-shared/src/utils/event'

import routerSelectors from './router'
import projectSelectors from './project'

export const org: Selector<RootState, INode_Org | null> = state => state.rbac.org
export const users: Selector<RootState, INode_User[]> = state => state.rbac.users
export const roleTree: Selector<RootState, ITree_Role | null> = state => state.rbac.roleTree
export const roleSummary: Selector<RootState, IData_UserSummary_FlatRoleObject[]> = state => state.rbac.roleSummary
export const eventTypes: Selector<RootState, IData_AppEvent_Type_Summary[]> = state => state.rbac.eventTypes
export const eventData: Selector<RootState, IRecord_AppEvent_With_UserOrg[]> = state => state.rbac.eventData
export const eventPagination: Selector<RootState, IData_Paginated_Results> = state => state.rbac.eventPagination

export const profile: Selector<RootState, IData_Org_With_Profile | null> = state => state.rbac.profile
export const profiles: Selector<RootState, IData_Org_With_Profile[]> = state => state.rbac.profiles
export const profilesLoaded: Selector<RootState, boolean> = state => state.rbac.profilesLoaded
export const magicTags: Selector<RootState, IData_MagicTag_Summary[]> = state => state.rbac.magicTags

export const network = networkGroup('rbac', [
  'loadOrg',
  'loadUsers',
  'addUser',
  'updateUser',
  'deleteUser',
  'addOrg',
  'updateOrg',
  'deleteOrg',
  'loadEvents',
  'updateProfile',
  'updateProfiles',
  'loadMagicTags',
])

export const userFormError = createSelector(
  network.addUser.error,
  network.updateUser.error,
  (addUser, updateUser) => addUser || updateUser,
)

export const orgFormError = createSelector(
  network.addOrg.error,
  network.updateOrg.error,
  (addOrg, updateOrg) => addOrg || updateOrg,
)

export const orgMap = createSelector(
  org,
  org => {
    const idMap: Record<string, INode_Org> = {}
    if(!org) return idMap
    const collectOrg = (o: INode_Org) => {
      idMap[o.id] = o
      o.orgs.forEach(collectOrg)
      o.adjacentOrgs.forEach(collectOrg)
    }
    collectOrg(org)
    return idMap
  }
)

export const viewingOrg = createSelector(
  orgMap,
  routerSelectors.params,
  (orgMap, params): INode_Org | null => {
    if(!params.orgid) return null
    return orgMap[params.orgid]
  }
)

export const childOrgs = createSelector(
  orgMap,
  routerSelectors.params,
  (orgMap, params): INode_Org[] => {
    if(!params.orgid) return []
    const org = orgMap[params.orgid]
    if(!org) return []
    return org.orgs
  }
)

export const roleSummaryTree = createSelector(
  roleTree,
  roleSummary,
  (roleTree, roleSummary): IData_RoleSummary_TreeNode[] => {
    if(!roleTree) return []
    return getRoleSummaryTree(
      roleTree,
      roleSummary,
      true,
    )
  }
)

export const roleSummaryTable = createSelector(
  roleTree,
  roleSummary,
  (roleTree, roleSummary): IData_RoleSummary_TreeNode[] => {
    if(!roleTree) return []
    return getRoleSummaryTree(
      roleTree,
      roleSummary,
      false,
    )
  }
)

export const eventTypeTree = createSelector(
  eventTypes,
  (eventTypes) => getEventTypeTree(eventTypes)
)

export const eventTypeTreeSelectOptions = createSelector(
  eventTypes,
  (eventTypes) => getEventTypeTreeSelectOptions(eventTypes)
)

export const appConfig = createSelector(
  projectSelectors.appConfig,
  (appConfig): IMeta_ProjectConfig_App_Config_Rbac | null => {
    const rbacApp = appConfig.find(config => config.namespace == 'rbac')
    if(!rbacApp || !rbacApp.config) return null
    const rbacConfig = rbacApp.config as IMeta_ProjectConfig_App_Config_Rbac
    return rbacConfig
  }
)

const DEFAULT_PROFILE_FORM = {
  schema: [],
  validation: {},
}

export const profileForm = createSelector(
  appConfig,
  (rbacConfig): IForm_Dynamic => {
    if(!rbacConfig || !rbacConfig.profile) return DEFAULT_PROFILE_FORM
    return {
      schema: rbacConfig.profile.schema.concat([{
        id: 'meta',
        title: 'Custom Fields',
        helperText: 'You can add whatever data you want to these rows, just click and type to add data',
        component: 'metadata',
      }]),
      validation: rbacConfig.profile.validation,
    }
  }
)

const DEFAULT_PROFILE_FIELDS = {
  name: '',
  image: '',
}

export const profileFields = createSelector(
  appConfig,
  (rbacConfig): {
    name: string,
    image: string,
  } => {
    if(!rbacConfig) return DEFAULT_PROFILE_FIELDS
    return {
      name: rbacConfig.nameField,
      image: rbacConfig.imageField,
    }
  }
)

export const flatProfiles = createSelector(
  profiles,
  (profiles): IData_Org_With_Profile[] => flattenOrgProfiles(profiles)
)

export const profileMap = createSelector(
  flatProfiles,
  (profiles): Record<string, IData_Org_With_Profile> => {
    return profiles.reduce<Record<string, IData_Org_With_Profile>>((all, org) => {
      all[org.id] = org
      return all
    }, {})
  }
)

export const getOrgNameFactory = createSelector(
  appConfig,
  profileMap,
  (config, profileOrgs): {
    (id: string): string,
  } => (id) => {
    const profileOrg = profileOrgs[id]
    if(!profileOrg) return id
    const nameField = config?.nameField
    if(!nameField) return profileOrg.name
    return profileOrg.profile[nameField] || profileOrg.name
  }
)


export const flatSortedProfiles = createSelector(
  profiles,
  getOrgNameFactory,
  (profiles, getOrgName): IData_Org_With_Profile[] => {
    const ret = flattenOrgProfiles(profiles)
    ret.sort((a, b) => {
      const aDepth = a.org_path.split('.')
      const bDepth = b.org_path.split('.')
      if (aDepth.length > bDepth.length) {
        return 1
      }
      else if (aDepth.length < bDepth.length) {
        return -1
      }
      const aName = getOrgName(a.id)
      const bName = getOrgName(b.id)
      if (aName > bName) {
        return 1
      }
      else if (aName < bName) {
        return -1
      }
      else {
        return 0
      }
    })
    return ret
  }
)

export const profileMetadataFields = createSelector(
  flatProfiles,
  (flatProfiles) => {
    const allFields = flatProfiles.reduce<Record<string, boolean>>((all, org) => {
      return Object.assign({}, all, org.profile.meta || {})
    }, {})
    return Object.keys(allFields)
  }
)

export const profileAdminColumns = createSelector(
  profileForm,
  profileMetadataFields,
  (profileForm, metaFields): IDataGrid2_Column<any>[] => {
    if(!profileForm) return []
    const flatSchema = flattenSchema(profileForm.schema)
    const formColumns = flatSchema
      // only include fields that 
      .filter(item => {
        if(!item.component) return true
        if(item.component == 'textarea') return true
        return false
      })
      .map(item => {
        const column: IDataGrid2_Column<any> = {
          name: item.id,
          header: item.title || item.id,
          defaultWidth: 200,
          sortable: false,
        }
        return column
      })
    const metaColumns = metaFields.map(field => {
      const column: IDataGrid2_Column<any> = {
        name: field,
        header: field,
        defaultWidth: 200,
        sortable: false,
      }
      return column
    })
    const baseColumns: IDataGrid2_Column<any>[] = [
      { name: 'id', header: 'Id', defaultWidth: 200, sortable: false, },
      { name: 'core_name', header: 'Org Name', defaultWidth: 200, sortable: false, },
    ]
    return baseColumns
      .concat(formColumns)
      .concat(metaColumns)
  }
)

export const profileAdminData = createSelector(
  flatProfiles,
  profileAdminColumns,
  (flatProfiles, columns) => {
    return flatProfiles
      .map(org => {
        const ret = Object.assign({}, org.profile)
        const meta = ret.meta
        delete(ret.meta)
        const returnData = Object.assign({}, ret, meta, {
          id: org.id,
          core_name: org.name,
        })
        columns.forEach(column => {
          if(!returnData[column.name]) returnData[column.name] = ''
        })
        return returnData
      })
      .sort((a, b) => {
        if(a.core_name > b.core_name) return 1
        else if(b.core_name > a.core_name) return -1
        else return 0
      })
  }
)

export default {
  org,
  users,
  roleTree,
  roleSummary,
  network,
  userFormError,
  orgFormError,
  orgMap,
  viewingOrg,
  getOrgNameFactory,
  childOrgs,
  roleSummaryTree,
  roleSummaryTable,
  eventTypes,
  eventData,
  eventPagination,
  eventTypeTree,
  eventTypeTreeSelectOptions,
  profileForm,
  profile,
  profiles,
  profilesLoaded,
  profileFields,
  flatProfiles,
  flatSortedProfiles,
  profileMap,
  profileMetadataFields,
  profileAdminColumns,
  profileAdminData,
  magicTags,
}
